import Grid from '@/global/components/ui/Grid'
import { BrasilApiCnpj } from '@/types'
import { CategoriaEnum } from '@acme/auth'
import { cpfValido, isCaepfValido, isCnpjValido } from '@acme/common'
import axios from 'axios'
import { FormField, FormFieldCheckbox } from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import { useAuth } from 'hooks'
import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { SelectColaboradores } from 'templates/Avaliacao/atoms/forms/SelectColaboradores'

interface IProps {
    faixaAtual: number
}

export function EditarClienteForm({ faixaAtual }: IProps) {
    const ref = useRef<HTMLDivElement>(null)
    const { watch, clearErrors, setValue, setError } = useFormContext()
    const {
        decoded: { categoria },
    } = useAuth()

    const [categoriaId, limiteFuncionarios] = watch([
        'categoriaId',
        'limiteFuncionarios',
    ])
    const criandoRepresentante = categoriaId === 7,
        faixaAlterada = limiteFuncionarios !== faixaAtual

    useEffect(() => {
        if (faixaAlterada) ref.current?.scrollIntoView({ behavior: 'smooth' })
    }, [faixaAlterada])

    const document = watch('cnpj')

    useEffect(() => {
        let timerDigitacaoDocumento: ReturnType<typeof setTimeout>
        const doc =
            document
                ?.toUpperCase()
                .replace(/[^A-Z0-9]/g, '')
                .slice(0, 14) || ''

        if (cpfValido(doc)) {
            clearErrors('cnpj')
            setValue(
                'cnpj',
                doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4'),
            )
        } else if (isCnpjValido(doc)) {
            axios
                .get<BrasilApiCnpj>(
                    `https://brasilapi.com.br/api/cnpj/v1/${doc}`,
                )
                .then(async ({ data }) => {
                    console.log(data)
                    if (!watch('razaoSocial')) {
                        setValue('razaoSocial', data.razao_social)
                    }
                    if (!watch('nomeFantasia')) {
                        setValue('nomeFantasia', data.nome_fantasia)
                    }
                    if (!watch('uf')) {
                        setValue('uf', data.uf)
                    }
                    if (!watch('cep')) {
                        setValue('cep', data.cep)
                    }
                    if (!watch('Cidade.cidade')) {
                        setValue('Cidade', {
                            cidade: data.municipio,
                            uf: data.uf,
                            ibge: data.codigo_municipio_ibge,
                        })
                    }
                    if (!watch('bairro')) {
                        setValue('bairro', data.bairro)
                    }
                    if (!watch('endereco')) {
                        setValue('endereco', data.logradouro)
                    }
                    if (!watch('numero')) {
                        setValue('numero', data.numero)
                    }
                    if (!watch('telefoneResponsavel')) {
                        setValue(
                            'telefoneResponsavel',
                            data.ddd_telefone_1?.toString(),
                        )
                    }
                    if (!watch('telefone')) {
                        setValue('telefone', data.ddd_telefone_2?.toString())
                    }
                    if (!watch('nomeResponsavel')) {
                        setValue(
                            'nomeResponsavel',
                            data.qsa[0]?.nome_socio ?? '',
                        )
                    }
                })
                .catch(e => {
                    console.error('Erro ao buscar dados do CNPJ:', e)
                })

            clearErrors('cnpj')
            setValue(
                'cnpj',
                doc.replace(
                    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
                    '$1.$2.$3/$4-$5',
                ),
            )
        } else if (isCaepfValido(doc)) {
            clearErrors('cnpj')
            setValue(
                'cnpj',
                doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4'),
            )
        } else {
            if (doc.length < 11 || (doc.length > 11 && doc.length < 14))
                clearErrors('cnpj')

            timerDigitacaoDocumento = setTimeout(() => {
                if (doc.length === 11) {
                    setError('cnpj', {
                        type: 'validate',
                        message: 'CPF inválido',
                    })
                } else if (doc.length === 14) {
                    setValue(
                        'cnpj',
                        doc.replace(
                            /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
                            '$1.$2.$3/$4-$5',
                        ),
                    )
                    setError('cnpj', {
                        type: 'validate',
                        message: 'CNPJ ou CAEPF inválido',
                    })
                }
            }, 500)
        }

        return () => clearTimeout(timerDigitacaoDocumento)
    }, [document])

    return (
        <Grid container rowSpacing={2} columnSpacing={3}>
            <Grid item xs={12}>
                <FormField name="nomeFantasia" label="Nome da empresa" />
            </Grid>
            <Grid item xs={12}>
                <FormField name="razaoSocial" label="Razão social" />
            </Grid>
            <Grid item sm={4} xs={12}>
                <FormField
                    name="cnpj"
                    label="Documento: (CPF, CNPJ ou CAEPF)"
                />
            </Grid>
            <Grid item sm={8} xs={12}>
                <FormField name="nomeResponsavel" label="Nome do responsável" />
            </Grid>
            <Grid item sm={8} xs={12}>
                <FormField name="emailResponsavel" label="Email" />
            </Grid>
            <Grid item sm={4} xs={12}>
                <FormField
                    name="telefoneResponsavel"
                    label="Telefone (com DDD)"
                    options={{
                        phone: true,
                        phoneRegionCode: 'BR',
                    }}
                />
            </Grid>
            <Grid item xs={6}>
                <SelectColaboradores disabled={criandoRepresentante} />
            </Grid>
            <Grid item xs={6}>
                <FormField
                    name="dtFimAtivacao"
                    label="Data final da ativação"
                    type="date"
                    disabled={
                        ![
                            CategoriaEnum.Administrador,
                            CategoriaEnum.Suporte,
                        ].includes(categoria)
                    }
                />
            </Grid>
            {[CategoriaEnum.Administrador, CategoriaEnum.Suporte].includes(
                categoria,
            ) && (
                <Grid item xs={12}>
                    <FormFieldCheckbox
                        name="suporteIntegrado"
                        label="Suporte integrado"
                    />
                </Grid>
            )}
            {faixaAlterada && (
                <Grid
                    item
                    xs={12}
                    ref={ref}
                    className="inline-flex items-center mb-4"
                >
                    <FormFieldCheckbox
                        name="alterarPlano"
                        label="Alterar plano do cliente"
                    />
                    <IconeAjuda>
                        Selecione essa opção caso deseje alterar o plano do
                        cliente para a faixa de colaboradores selecionada.
                        <br />
                        <br />A periodicidade do plano não será alterada, caso
                        deseje alterar a periodicidade, será necessário fazer
                        login com o usuário do cliente e alterar o plano
                        diretamente no painel do cliente.
                        <br />
                        <br />O método de pagamento utilizado será boleto.
                    </IconeAjuda>
                </Grid>
            )}
        </Grid>
    )
}
